import { useState } from "react";
import { toast } from "sonner";
import { Check, X, Minus, Plus, Target } from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { api, web, errMsg, toList } from "../../lib/api";
import { Panel, Loader, ErrorNote, Empty, shortDate } from "./kit";
import { HotZones } from "./HotZones";
import { classify } from "../../lib/court";

const ZONE = { rim: "Rim", paint: "Paint", midrange: "Mid-range", corner3: "Corner 3", wing3: "Wing 3", top3: "Top 3" };

const pctText = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);

export const CourtLines = () => (
  <g fill="none" stroke="currentColor" strokeWidth="2.5" className="text-slate-500">
    <rect x="2" y="2" width="496" height="466" rx="4" />
    <rect x="190" y="2" width="120" height="188" />
    <circle cx="250" cy="190" r="60" />
    <path d="M52.5 2 L52.5 52.5 A197.5 197.5 0 0 0 447.5 52.5 L447.5 2" className="text-[#39FF14]/60" stroke="currentColor" strokeWidth="3" />
    <line x1="220" y1="40" x2="280" y2="40" strokeWidth="4" className="text-slate-300" stroke="currentColor" />
    <circle cx="250" cy="52.5" r="7.5" className="text-[#FF3B30]" stroke="currentColor" />
    <path d="M190 468 A60 60 0 0 1 310 468" />
  </g>
);

const Marker = ({ s, onClick }) => {
  const cx = s.x * 10, cy = s.y * 10;
  const label = `${s.made ? "Made" : "Missed"} ${s.points}PT · ${ZONE[s.zone] ?? ""}${s.opponent ? ` · vs ${s.opponent}` : ""}`;
  return (
    <g data-testid={`shot-marker-${s.id}`} onClick={onClick ? (e) => { e.stopPropagation(); onClick(s); } : undefined} className={onClick ? "cursor-pointer" : ""}>
      <title>{label}</title>
      <circle cx={cx} cy={cy} r="13" fill="transparent" />
      {s.made
        ? <circle cx={cx} cy={cy} r="9" fill="#39FF14" fillOpacity="0.85" stroke="#0A0E1A" strokeWidth="2" />
        : <path d={`M${cx - 7} ${cy - 7} L${cx + 7} ${cy + 7} M${cx + 7} ${cy - 7} L${cx - 7} ${cy + 7}`} stroke="#FF3B30" strokeWidth="4" strokeLinecap="round" />}
    </g>
  );
};

const PendingPicker = ({ p, busy, onPick, onCancel }) => (
  <div data-testid="shot-pending" style={{ left: `${(p.x / 50) * 100}%`, top: `${(p.y / 47) * 100}%` }}
    className={`absolute z-10 -translate-x-1/2 ${p.y > 30 ? "-translate-y-[calc(100%+14px)]" : "translate-y-3.5"} rounded-2xl border border-[#1E293B] bg-[#0A0E1A]/95 p-2 shadow-2xl backdrop-blur-md`}>
    <p className="mb-1.5 whitespace-nowrap px-1 text-center font-mono-gd text-[11px] uppercase tracking-wider text-slate-300">{p.points}PT · {ZONE[p.zone]}</p>
    <div className="flex gap-1.5">
      <button data-testid="shot-made-button" disabled={busy} onClick={() => onPick(true)} className="inline-flex items-center gap-1 rounded-full bg-[#39FF14] px-3 py-1.5 text-xs font-bold text-[#0A0E1A] transition-transform active:scale-95 disabled:opacity-50"><Check className="h-3.5 w-3.5" />Made</button>
      <button data-testid="shot-missed-button" disabled={busy} onClick={() => onPick(false)} className="inline-flex items-center gap-1 rounded-full bg-[#FF3B30] px-3 py-1.5 text-xs font-bold text-white transition-transform active:scale-95 disabled:opacity-50"><X className="h-3.5 w-3.5" />Missed</button>
      <button data-testid="shot-cancel-button" onClick={onCancel} aria-label="Cancel" className="rounded-full px-2 text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
    </div>
  </div>
);

export const Court = ({ shots, pending, busy, onSpot, onPick, onCancel, onShot }) => {
  const click = (e) => {
    if (!onSpot) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.min(50, Math.max(0, ((e.clientX - r.left) / r.width) * 50));
    const y = Math.min(47, Math.max(0, ((e.clientY - r.top) / r.height) * 47));
    onSpot(x, y);
  };
  return (
    <div className="relative mx-auto w-full max-w-xl">
      <svg data-testid="shot-court" viewBox="0 0 500 470" onClick={click} className={`block w-full rounded-2xl bg-[#0F172A] ${onSpot ? "cursor-crosshair" : ""}`}>
        <CourtLines />
        {shots.map((s) => <Marker key={s.id} s={s} onClick={onShot} />)}
        {pending && <circle cx={pending.x * 10} cy={pending.y * 10} r="11" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeDasharray="5 4" />}
      </svg>
      {pending && <PendingPicker p={pending} busy={busy} onPick={onPick} onCancel={onCancel} />}
    </div>
  );
};

const Stat = ({ label, value, sub, accent, testid }) => (
  <div data-testid={testid} className={`rounded-xl border px-3 py-2.5 ${accent ? "border-[#39FF14]/50 bg-[#39FF14]/10" : "border-[#1E293B] bg-[#0F172A]"}`}>
    <p className="font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
    <p className={`font-mono-gd text-xl font-bold ${accent ? "text-[#39FF14]" : "text-white"}`}>{value}</p>
    {sub && <p className="font-mono-gd text-[11px] text-slate-400">{sub}</p>}
  </div>
);

export const ShotStats = ({ s = {} }) => (
  <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
    <Stat testid="shot-stat-ts" label="TS%" value={pctText(s.ts_pct)} sub={`${s.pts ?? 0} pts`} accent />
    <Stat testid="shot-stat-fg" label="FG" value={pctText(s.fg_pct)} sub={`${s.fgm ?? 0}/${s.fga ?? 0}`} />
    <Stat testid="shot-stat-2pt" label="2PT" value={pctText(s.fg2_pct)} sub={`${s.fg2m ?? 0}/${s.fg2a ?? 0}`} />
    <Stat testid="shot-stat-3pt" label="3PT" value={pctText(s.fg3_pct)} sub={`${s.fg3m ?? 0}/${s.fg3a ?? 0}`} />
    <Stat testid="shot-stat-ft" label="FT" value={pctText(s.ft_pct)} sub={`${s.ftm ?? 0}/${s.fta ?? 0}`} />
    <Stat testid="shot-stat-efg" label="eFG%" value={pctText(s.efg_pct)} />
  </div>
);

const FtButton = ({ testid, onClick, busy, children, tone }) => (
  <button data-testid={testid} disabled={busy} onClick={onClick}
    className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${tone}`}>{children}</button>
);

const FreeThrows = ({ s, busy, onFt }) => (
  <div data-testid="ft-controls" className="flex flex-wrap items-center gap-2 rounded-2xl border border-[#1E293B] bg-[#0F172A] px-4 py-3">
    <span className="mr-auto font-mono-gd text-xs uppercase tracking-wider text-slate-300">Free throws <span data-testid="ft-count" className="ml-2 text-base font-bold text-white">{s?.ftm ?? 0}/{s?.fta ?? 0}</span></span>
    <FtButton testid="ft-made-add" busy={busy} onClick={() => onFt(true, 1)} tone="border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10"><Plus className="h-3.5 w-3.5" />Made</FtButton>
    <FtButton testid="ft-miss-add" busy={busy} onClick={() => onFt(false, 1)} tone="border-[#FF3B30]/50 text-red-200 hover:bg-[#FF3B30]/10"><Plus className="h-3.5 w-3.5" />Miss</FtButton>
    <FtButton testid="ft-made-remove" busy={busy || !s?.ftm} onClick={() => onFt(true, -1)} tone="border-[#1E293B] text-slate-300 hover:text-white"><Minus className="h-3.5 w-3.5" />Made</FtButton>
    <FtButton testid="ft-miss-remove" busy={busy || !((s?.fta ?? 0) - (s?.ftm ?? 0))} onClick={() => onFt(false, -1)} tone="border-[#1E293B] text-slate-300 hover:text-white"><Minus className="h-3.5 w-3.5" />Miss</FtButton>
  </div>
);

const gameLabel = (g) => `${shortDate(g.date)} · vs ${g.opponent ?? "TBA"}`;

export const ViewToggle = ({ value, onChange }) => (
  <div data-testid="shot-view-toggle" className="inline-flex rounded-full border border-[#1E293B] bg-[#0F172A] p-1">
    {[["shots", "Shots"], ["zones", "Hot zones"]].map(([v, label]) => (
      <button key={v} data-testid={`shot-view-${v}`} onClick={() => onChange(v)}
        className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${value === v ? "bg-[#39FF14] text-[#0A0E1A]" : "text-slate-300 hover:text-white"}`}>{label}</button>
    ))}
  </div>
);

/** Shot chart for one player. Coaches (tid + canEdit) log shots per game; recruiters see it read-only. */
export const ShotChart = ({ tid, pid, canEdit = false }) => {
  const [gid, setGid] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ftLive, setFtLive] = useState(null);
  const [view, setView] = useState("shots");
  const coach = !!tid;
  const path = coach ? `/teams/${tid}/players/${pid}/shots${gid ? `?game_id=${gid}` : ""}` : `/recruiter/players/${pid}/shots${gid ? `?game_id=${gid}` : ""}`;
  const chart = useApi(pid && path, web);
  const teamGames = useApi(coach && canEdit && `/teams/${tid}/games`, api);
  const charted = Array.isArray(chart.data?.games) ? chart.data.games : [];
  const options = coach && canEdit
    ? toList(teamGames.data, ["games"]).map((g) => ({ game_id: g.id, date: g.date, opponent: g.opponent })).sort((a, b) => String(b.date ?? "").localeCompare(String(a.date ?? "")))
    : charted;
  const logging = canEdit && !!gid && view === "shots";
  const shots = Array.isArray(chart.data?.shots) ? chart.data.shots : [];
  const summary = chart.data?.summary;

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); chart.reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const spot = (x, y) => { const [points, zone] = classify(x, y); setPending({ x, y, points, zone }); };
  const pick = (made) => run(async () => { await web.post(`/teams/${tid}/games/${gid}/shots`, { player_id: pid, x: pending.x, y: pending.y, made }); setPending(null); });
  const removeShot = (s) => {
    if (!window.confirm(`Delete this ${s.made ? "made" : "missed"} ${s.points}PT shot? The box score updates too.`)) return;
    run(() => web.delete(`/teams/${tid}/shots/${s.id}`), "Shot removed");
  };
  const ft = (made, delta) => run(async () => {
    const { data } = await web.post(`/teams/${tid}/games/${gid}/free-throws`, { player_id: pid, made, delta });
    setFtLive({ gid, ...data });
  });
  const ftShown = ftLive?.gid === gid ? { ...summary, ...ftLive } : summary;

  return (
    <Panel data-testid="shot-chart">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-display text-xl font-bold uppercase text-white"><Target className="h-5 w-5 text-[#39FF14]" />Shot chart</h3>
          <p className="text-sm text-slate-400">{view === "zones" ? "Each zone is colored by FG% compared to typical high school shooting from there." : logging ? "Tap the court where the shot was taken, then pick Made or Missed. Tap a shot to delete it." : gid ? "Shots from this game." : "Every charted shot this season."}</p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
        <ViewToggle value={view} onChange={(v) => { setView(v); setPending(null); }} />
        <select data-testid="shot-game-select" value={gid} onChange={(e) => { setGid(e.target.value); setPending(null); }}
          className="w-full rounded-full border border-[#1E293B] bg-[#162032] px-4 py-2.5 text-sm text-white sm:w-auto">
          <option value="">Season (all games)</option>
          {options.map((g) => <option key={g.game_id} value={g.game_id}>{gameLabel(g)}</option>)}
        </select>
        </div>
      </div>
      {chart.loading && !chart.data ? <Loader /> : chart.error ? <ErrorNote error={chart.error} /> : (
        <div className="space-y-5">
          <ShotStats s={summary} />
          {logging && <FreeThrows s={ftShown} busy={busy} onFt={ft} />}
          {!shots.length && !logging ? (
            <Empty title="No shots charted yet" text={canEdit ? "Pick a game above to start charting shots." : "This player's coach hasn't charted shots yet."} />
          ) : view === "zones" ? (
            <HotZones shots={shots} lines={<CourtLines />} />
          ) : (
            <Court shots={shots} pending={logging ? pending : null} busy={busy} onSpot={logging ? spot : undefined}
              onPick={pick} onCancel={() => setPending(null)} onShot={logging ? removeShot : undefined} />
          )}
          {view === "shots" && <div className="flex flex-wrap items-center gap-4 font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-[#39FF14]" />Made</span>
            <span className="flex items-center gap-1.5"><X className="h-3.5 w-3.5 text-[#FF3B30]" />Missed</span>
            <span>3PT line: 19 ft 9 in (high school)</span>
            <span>TS% = PTS / (2 x (FGA + 0.44 x FTA)), charted games</span>
          </div>}
        </div>
      )}
    </Panel>
  );
};
